import { OrganizationContext } from '../../../components/contexts/organization';
import { SearchFormValues } from '../detail/UsageStatisticDetail';
import {
  PollShopCouponUsageJsonResponse,
  useInvokeShopCouponUsageCsvLazyQuery,
  useInvokeShopCouponUsageJsonLazyQuery,
  usePollShopCouponUsageCsvLazyQuery,
  usePollShopCouponUsageJsonLazyQuery,
} from '../../../graphql/generated';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/js';
import { downloadCsvFileWithBom } from '../../../util/download';
import { formatJstDate } from '../../../util';

const Limit = 25;

export type CouponUsageStatisticDetail = {
  organization?: OrganizationContext;
  loading: boolean;
  onSearch: (condition: SearchFormValues) => void;
  onCsvOut: (condition: SearchFormValues) => void;
  csvLoading: boolean;
  data?: PollShopCouponUsageJsonResponse;
  onFetchMore: (condition: SearchFormValues) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  allAcquired: boolean;
};

type UseCouponUsageStatisticDetailProps = {
  couponMasterId: string;
};

export const useCouponUsageStatisticDetail = ({
  couponMasterId,
}: UseCouponUsageStatisticDetailProps): CouponUsageStatisticDetail => {
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();
  const [fetching, setFetching] = useState<boolean>(false);
  const [allAcquired, setAllAcquired] = useState<boolean>(false);

  const handleError = (): void => {
    setLoading(false);
    setCsvLoading(false);
    enqueueSnackbar('サーバーエラーが発生した', {
      variant: 'error',
      autoHideDuration: 3000,
    });
  };

  const [pollJsonQuery, statusContext] = usePollShopCouponUsageJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first', // これをつけないと fetchMore 時に nextTokenなしで取得しにいってしまう
    pollInterval: 5000,
  });

  // 利用状況集計 取得
  // ApolloClient で ポーリングとfetchMoreを同時に利用できないため分ける
  const [getJsonQuery, getJsonQueryContext] = usePollShopCouponUsageJsonLazyQuery({
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (Number(data?.pollShopCouponUsageJson?.nodes?.length) < Limit) {
        setAllAcquired(true);
      }
      setLoading(false);
    },
    onError: () => handleError(),
  });

  const [invokeJsonQuery] = useInvokeShopCouponUsageJsonLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollJsonQuery({
        variables: {
          queryExecutionId: data.invokeShopCouponUsageJson.queryExecutionId,
        },
      });
      setOffset(offset + Limit);
    },
    onError: () => handleError(),
  });

  // 利用状況一覧 CSV URL ポーリング
  const [pollCsvUrlQuery, statusCsvUrlContext] = usePollShopCouponUsageCsvLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    pollInterval: 5000,
  });

  // 利用状況CSV取得 invoke
  const [invokeCsvQuery] = useInvokeShopCouponUsageCsvLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      pollCsvUrlQuery({
        variables: {
          queryExecutionId: data.invokeShopCouponUsageCsv.queryExecutionId,
        },
      });
    },
    onError: () => handleError(),
  });

  // 検索実行
  const onSearch = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setOffset(0);
      setLoading(true);
      invokeJsonQuery({
        variables: {
          couponMasterId: couponMasterId,
          usageStartDate: data.usageStartDate ? formatJstDate(new Date(data.usageStartDate).toISOString()) : undefined,
          usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
          limit: Limit,
          offset: 0,
        },
      });
    },
    [invokeJsonQuery]
  );

  // CSV出力
  const onCsvOutSubmit = useCallback(
    (data: SearchFormValues) => {
      if (!organization) {
        return;
      }
      setCsvLoading(true);
      invokeCsvQuery({
        variables: {
          couponMasterId: couponMasterId,
          usageStartDate: data.usageStartDate ? formatJstDate(new Date(data.usageStartDate).toISOString()) : undefined,
          usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
        },
      });
    },
    [organization, invokeCsvQuery]
  );

  // JSONクエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusContext?.data?.pollShopCouponUsageJson?.status;
    const hasError = statusContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusContext && statusContext.stopPolling) {
        statusContext.stopPolling();
      }
    }

    // 正常に終了した場合は、データフェッチ用のクエリを呼び出してステータスを終了にする
    if (
      status === 'SUCCEEDED' &&
      statusContext &&
      statusContext.data &&
      statusContext.data.pollShopCouponUsageJson &&
      statusContext.data.pollShopCouponUsageJson.nodes
    ) {
      if (
        fetching &&
        organization &&
        getJsonQueryContext &&
        getJsonQueryContext.fetchMore &&
        getJsonQueryContext.variables
      ) {
        setLoading(true);
        getJsonQueryContext.fetchMore({
          variables: statusContext.variables,
          updateQuery: (prevQuery, { fetchMoreResult }) => {
            setLoading(false);
            if (!fetchMoreResult) {
              return prevQuery;
            }
            if (Number(fetchMoreResult?.pollShopCouponUsageJson?.nodes?.length) < Limit) {
              setAllAcquired(true);
              enqueueSnackbar('全件取得済みです', { variant: 'success' });
            } else {
              enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
            }
            return {
              pollShopCouponUsageJson: {
                __typename: fetchMoreResult.pollShopCouponUsageJson.__typename,
                queryExecutionId: fetchMoreResult.pollShopCouponUsageJson.queryExecutionId,
                status: fetchMoreResult.pollShopCouponUsageJson.status,
                nodes: [
                  ...(prevQuery.pollShopCouponUsageJson.nodes || []),
                  ...(fetchMoreResult.pollShopCouponUsageJson.nodes || []),
                ],
                pageInfo: fetchMoreResult.pollShopCouponUsageJson.pageInfo,
              },
            };
          },
        });
        setFetching(false);
      } else {
        getJsonQuery({
          variables: statusContext.variables,
        });
        enqueueSnackbar('集計処理が正常に終了しました', { variant: 'success' });
      }
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
      });
      Bugsnag.notify(statusContext.error || new Error('polling error'));
    }
  }, [statusContext.data?.pollShopCouponUsageJson.status, statusContext?.error]);

  // CSV URL クエリ出力をポーリングする
  // ポーリングクエリの onComplete は呼ばれないので useEffect でやるしかない
  useEffect(() => {
    const status = statusCsvUrlContext?.data?.pollShopCouponUsageCsv?.status;
    const hasError = statusCsvUrlContext?.error;

    // 終了系ステータスの場合、ポーリングを止める
    if (status === 'CANCELLED' || status === 'FAILED' || status === 'SUCCEEDED' || hasError) {
      if (statusCsvUrlContext && statusCsvUrlContext.stopPolling) {
        statusCsvUrlContext.stopPolling();
      }
    }

    // 正常に終了した場合は、CSVファイルをダウンロード
    if (
      status === 'SUCCEEDED' &&
      statusCsvUrlContext &&
      statusCsvUrlContext.data &&
      statusCsvUrlContext.data.pollShopCouponUsageCsv
    ) {
      enqueueSnackbar('集計処理が正常に終了しました。CSVファイルをダウンロードします。', { variant: 'success' });
      setCsvLoading(false);
      if (statusCsvUrlContext.data.pollShopCouponUsageCsv?.preSignedUrl)
        downloadCsvFileWithBom(statusCsvUrlContext.data.pollShopCouponUsageCsv?.preSignedUrl);
    } else if (status === 'FAILED' || status === 'CANCELLED' || hasError) {
      setCsvLoading(false);
      enqueueSnackbar('集計処理でエラーが発生しました', {
        variant: 'error',
      });
      Bugsnag.notify(statusCsvUrlContext.error || new Error('polling error'));
    }
  }, [
    statusCsvUrlContext.data?.pollShopCouponUsageCsv.status,
    statusCsvUrlContext.data?.pollShopCouponUsageCsv.preSignedUrl,
    statusCsvUrlContext?.error,
  ]);

  // さらに読み込む
  const onFetchMore = useCallback(
    async (data: SearchFormValues) => {
      if (statusContext && statusContext.stopPolling) {
        statusContext?.stopPolling();
      }
      if (organization && statusContext && statusContext.fetchMore && statusContext.variables) {
        setLoading(true);
        setFetching(true);
        await invokeJsonQuery({
          variables: {
            couponMasterId: couponMasterId,
            usageStartDate: data.usageStartDate
              ? formatJstDate(new Date(data.usageStartDate).toISOString())
              : undefined,
            usageEndDate: data.usageEndDate ? formatJstDate(new Date(data.usageEndDate).toISOString()) : undefined,
            limit: Limit,
            offset: offset,
          },
        });
      }
    },
    [statusContext, getJsonQueryContext, organization]
  );
  return {
    organization,
    loading,
    onSearch,
    onCsvOut: onCsvOutSubmit,
    csvLoading,
    data: getJsonQueryContext.data?.pollShopCouponUsageJson,
    onFetchMore,
    open,
    setOpen,
    allAcquired,
  };
};
